import { RefObject, useEffect } from 'react';

type Event = MouseEvent | TouchEvent;
type Handler = (event: Event) => void;

/**
 * Optional: CSS selectors for elements that should NOT trigger the handler when clicked
 * (e.g. modal/alert overlays). Use when click-outside would wrongly close menu during alert.
 */
export const useOnClickOutside = <T extends HTMLElement = HTMLElement>(
	ref: RefObject<T>,
	handler: Handler,
	options?: { ignoreSelectors?: string[] }
) => {
	const ignoreSelectors = options?.ignoreSelectors;

	useEffect(() => {
		const listener = (event: Event) => {
			// Do nothing if clicking ref's element or descendent elements
			if (!ref.current || ref.current.contains(event.target as Node)) {
				return;
			}
			// Do not trigger when click is inside ignored elements (e.g. alert/modal)
			const target = event.target as Element;
			if (
				ignoreSelectors?.length &&
				target?.closest &&
				ignoreSelectors.some((sel) => target.closest(sel))
			) {
				return;
			}
			handler(event);
		};
		document.addEventListener('mousedown', listener);
		// document.addEventListener("touchstart", listener);
		return () => {
			document.removeEventListener('mousedown', listener);
			// document.removeEventListener("touchstart", listener);
		};
	}, [ref, handler, ignoreSelectors]);
};
